/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
define([
	'jquery',
	'../lib/@waca/dashboard-common/dist/core/Collection',
	'./FilterEntry',
	'../widgets/livewidget/nls/StringResources',
	'underscore'
],
function($, Collection, FilterEntry, stringResources, _) {
	'use strict';


	//Defines the type of filters as one of 'category' (discrete) or 'ordinal' (range).
	//Note that these terms are equivalent to columnTypes returned by getType() for slots/mappings.
	var FILTERTYPE_CATEGORY = 'category'; //Used for category 'discrete' filters.
	var FILTERTYPE_BINS = 'bins'; //Used for category 'discrete' filters.
	var FILTERTYPE_SEARCH = 'search'; //Used for category 'discrete' filters.
	var FILTERTYPE_MODEL = 'filter'; // Used for FM package model filters

	var FILTERTYPE_ORDINAL = 'ordinal'; //Used for ordinal 'range' filters.

	/**
		 * The filters associated with the current datasource query.
		 * Includes Methods to add, remove, clear and produce the query in spec form.
		 */
	// Declare the class
	var Filters = null;
	Filters = Collection.extend({

		// The class of objects that this collection manages
		modelClass: FilterEntry,

		FILTERTYPE_ORDINAL: FILTERTYPE_ORDINAL,
		FILTERTYPE_CATEGORY: FILTERTYPE_CATEGORY,
		FILTERTYPE_BINS: FILTERTYPE_BINS,
		FILTERTYPE_SEARCH: FILTERTYPE_SEARCH,

		init: function() {
			Filters.inherited('init', this, arguments);
			this._compoundFiltersFromJSON();
			this.updateNeeded = false;
		},

		_takeSnapShot: function(snapshot) {
			if (!this.updateNeeded){
				this.updateNeeded = true;
				//take a snapshot of the current collections
				this._snapshot = snapshot || this.toJSON();
			}
		},

		_filterModificationComplete: function(options = {}) {

			if (this.updateNeeded && !options.silent) {
				this.updateNeeded = false;
				// TODO: clean this once we move everything to api undo/redo events
				if(!options.isApiUndoRedoEvent) {
					//Trigger a collection reset change.
					var event = {
						name: 'reset',
						eventName: 'reset',
						collection: this,
						data: options.payloadData,
						prevValue: this._snapshot,
						value: this.toJSON(),
						sender: options.sender || this
					};
					event.senderContext = {
						applyFn: (value, sender, name, payloadData) => {
							var options = {
								sender: sender
							};
							if(payloadData) {
								options.payloadData = payloadData;
							}
							this.reset(value, options);
						}
					};
					this.trigger('reset', event);
				}
			}
		},

		/**
			 * If filter contents have been modified since the last time this method was called, a change:filters event will be triggered.
			 *
			 * This is a bit like a simple transaction mechanism but goes 1 step further in that no event is called if things have not changed.
			 *
			 * This is most useful when performing sets of calls to updateFilterEntry() or using the 'WithoutTriggerEvent' SELECT_API's
			 * to signify that an event should be sent.  This also guards against calling updates when, for example, a bar that is already selected is clicked again.
			 */
		allFilterModificationComplete: function(options = {}) {
			this._filterModificationComplete(options);
			return options.filterEntry;
		},

		/**
			 * FILTERAPI:
			 * @param oColumOrId - a columnId (or visColumn).
			 * @param filterType - one of 'category' or 'ordinal' (same as getType() for a slot or mapping)
			 * @param aggregationType (required for FILTERTYPE_ORDINAL only, ignored otherwise) needed for FILTERTYPE_ORDINAL.
			 * @param preOrPost - (optional) Used to allow a user to specify a pre-aggregate range filter.
			 * @return an id and related information needed to access/initialize a filter of the specified type
			 * {
			 *	id: <filterEntryId> (For category type filters, this is the same as the columnId, for ordinal types it is columnId_aggregationType
			 *  columnId: (same as the columnID passed in)
			 *  aggregationType: (Only returned for ordinal types as aggregationType should not be part of a category key or internal values.
			 * }
			 */
		getKey: function(oColumnOrId, filterType, aggregationType, preOrPost) {
			if (oColumnOrId === null) {
				return null;
			}
			if (filterType === FILTERTYPE_ORDINAL) {
				return this.getOrdinalFilterKey(oColumnOrId, aggregationType, preOrPost);
			}
			//A discrete type filter with a 'category key'...(just the columnId).
			return this.getCategoryFilterKey(oColumnOrId);
		},

		/**
			 * FILTERAPI:
			 * @param oColumnOrId - a columnId (or visColumn)
			 * @param aggregationType - the aggregation type.
			 * @param preOrPost - (optional) Used to allow a user to specify a pre-aggregate range filter.
			 * @returns an ordinal type filter key (id and related information for a filterEntry)
			 */
		getOrdinalFilterKey: function(oColumnOrId, aggregationType, preOrPost) {
			return {
				//TODO: preOrPost should be part of the id so that you could have both a pre and post aggregation range filter
				//in the same collection, on the same item. (Fairly rare and would only apply for pinning).
				id: FilterEntry.prototype.determineIdValue(this._columnId(oColumnOrId), aggregationType),
				columnId: this._columnId(oColumnOrId),
				aggregationType: aggregationType,
				filterType: FILTERTYPE_ORDINAL,
				preOrPost: preOrPost || 'post' //default is post.
			};
		},

		/**
		 * FILTERAPI:
		 * @param oColumnOrId - a columnId (or visColumn).
	     * @param uniqueId - a unique id for filter (used for filter bins)
		 * @return an id and related information needed to access/initialize a column-type filter (which is just the columnId).
		 */
		getCategoryFilterKey: function(oColumnOrId, uniqueId) {
			return {
				id: uniqueId ? uniqueId : FilterEntry.prototype.determineIdValue(this._columnId(oColumnOrId)),
				columnId: this._columnId(oColumnOrId),
				filterType: FILTERTYPE_CATEGORY
			};
		},

		getModelFilterKey: function(oColumnOrId) {
			return {
				id: FilterEntry.prototype.determineIdValue(this._columnId(oColumnOrId)),
				columnId: this._columnId(oColumnOrId),
				filterType: FILTERTYPE_MODEL
			};
		},

		getSearchFilterKey: function(oColumnOrId) {
			return {
				id: FilterEntry.prototype.determineIdValue(this._columnId(oColumnOrId)),
				columnId: this._columnId(oColumnOrId),
				filterType: FILTERTYPE_SEARCH
			};
		},

		/**
			 * @param itemIds - the array of itemIds (columnIds) that the dataPoint is made up of.
			 * @param idSuffix (optional) - a numeric suffix (applies to additional Filters on a given tuple usu. from keepexclude actions)
			 * @returns the idValue for this dataPoint filter.
			 */
		getDataPointFilterKey: function(itemIds, idSuffix) {
			return FilterEntry.prototype.determineIdValue(JSON.stringify(itemIds), null, idSuffix);
		},

		/**
			 * FILTERAPI:
			 * @returns the filterEntry that corresponds to the passed in filterEntryKey (as returned by getKey above).
			 */
		getFilterEntry: function(filterEntryKey) {
			this._checkFilterEntryKey(filterEntryKey);
			return this.get(filterEntryKey.id);
		},

		/**
			 * FILTERAPI:
			 * @param filterEntryKey entries to search for by baseId (id), where filterEntries match the id plus a suffix OR columnId.
			 * @returns all filterEntries (including additional keep/exclude filters) that correspond to the passed in filterEntryKey
			 * (note that all AdditionalFilters use the same base key with an idSuffix).
			 */
		getFilterEntries: function(filterEntryKey) {
			this._checkFilterEntryKey(filterEntryKey);
			var matchField = (filterEntryKey.id) ? 'id' : 'columnId';
			var filterEntries = [];
			if (filterEntryKey[matchField]) {
				this.each(function(filterEntry) {
					if (filterEntry[matchField].indexOf(filterEntryKey[matchField]) === 0) {
						filterEntries.push(filterEntry);
					}
				});
			} else if (this.logger) {
				this.logger.debug('WARNING: getFilterEntries only supports a baseId (id) or columnId');
			}
			return filterEntries;
		},

		/**
			 * A filter entry key is valid if it is non-null, has a columnId and, if ordinal, also has an aggregation type.
			 */
		_hasValidFilterType: function(oKey) {
			return ((oKey.filterType === 'category') || (oKey.filterType === 'ordinal' && oKey.aggregationType));
		},

		/**
			 * A known filter entry key may also be specified directly via the id member of oKey.
			 */
		_checkFilterEntryKey: function(oKey) {
			if (oKey) {
				if (oKey.id) {
					//An absolute key can also be specified
					return;
				}
				var bColumnIdIsValid = (oKey.columnId !== undefined) && (oKey.columnId !== null);
				if (bColumnIdIsValid && this._hasValidFilterType(oKey)) {
					return;
				}
			}
			throw 'Invalid key used to access filters (_checkFilterEntryKey)';
		},

		/**
			 * add/update a new dataPoint filter for the filterCollection.
			 * @param itemIds - An array of itemIds (columnIds) of the items that make up the points. (eg  [X.Year,X.Products])
			 * @param filterDefinition - a definition of the dataPoint filter which includes the following members:
			 *				values: An an array of dataPoints (and each dataPoint is an array of {u: d:}.... 1 entry per itemId)
			 *							eg [
			 *									[{u:2005:d:2005},{u:cars:d:cars}],
			 *									[{u:2006:d:2006},{u:trucks:d:trucks}],
			 *									[[u:2005:d:2005},{u:buses:d:buses}]
			 *							]
			 *				exclude: true if this is an exclude type dataPoint filter,
			 *				action: denotes what action is being performed (eg: keepexclude)
			 * @param optionsArg - options (if passed, they will be used but will always be silent)
			 */
		addDataPointFilter: function(itemIds, binningAPIs, filterDefinition, optionsArg) {
			this._takeSnapShot();
			var options = _.clone(optionsArg || {});
			//all updates in this method are silent (expect an endTransaction or allFilterModificationComplete to be performed.)
			options.silent = true;

			var id = this.getDataPointFilterKey(itemIds);

			var newDataPointFilterEntries = [];
			_.each(filterDefinition.values, function(dataPoint) {
				newDataPointFilterEntries.push(this._createOneDataPoint(itemIds, binningAPIs, dataPoint, filterDefinition));
			}.bind(this));
			var newFilter = this._createRootDataPointFilterEntry(id, newDataPointFilterEntries, filterDefinition);
			if (this.get(newFilter.id)) {
				//If a filter exists for this key, replace it with the new filter.
				//note: this works for 'additional filters' as well because a unique key will be generated so there should never be one at the new key.
				this.remove(newFilter.id, options);
			}
			this.add(newFilter, options);
		},

		//The 'root' of a dataPoint filter is either an 'or' for all of the points in the filter
		//or a 'not' which contains an 'or' for exclude type dataPoint filters.
		_createRootDataPointFilterEntry: function(id, filterEntry, filterDefinition) {
			var orSpec = {
				dataPointId: id,
				operator: 'or',
				values: filterEntry,
				visualizationId: this.visId
			};
			var rootFilterEntry = this._createNewFilterEntry(filterDefinition, orSpec);

			if (filterDefinition.exclude) {
				var notSpec = {
					dataPointId: id,
					operator: 'not',
					values: rootFilterEntry
				};
				rootFilterEntry = this._createNewFilterEntry(filterDefinition, notSpec);
			}

			return rootFilterEntry;
		},

		//A single dataPoint that makes up a dataPoint filter will be a set of item/value pairs that form a tuple 'anded together'
		_createOneDataPoint: function(itemIds, binningAPIs, selectedDataPoint, filterDefinition) {
			var tupleFilterEntries = []; //Holds the tuple (which is an array of tuple parts that are anded together.)
			_.each(selectedDataPoint, function(selection, itemIdx) {
				var filterEntrySpec = {
					columnId: itemIds[itemIdx],
					values: [selection],
					operator: 'in',
					visualizationId: filterDefinition.visualizationId || this.visId,
					type: filterDefinition.type
				};
				var itemId = itemIds[itemIdx];
				if (binningAPIs) {
					var binning = binningAPIs.find(function (binningAPI) {
						return binningAPI.columnId === itemId || binningAPI.itemId === itemId;
					});
					if (binning) {
						filterEntrySpec.filterBins = true;
						if (!binning.dataItemId) {
							var binningInfo = binning.binningAPI;
							filterEntrySpec.binsLabel = binningInfo.label;
							filterEntrySpec.id = binning.id;
						} else {
							filterEntrySpec.binsLabel = binning.label;
							filterEntrySpec.id = binning.dataItemId;
						}
					}
				}
				var tuplePartFilterEntry = new FilterEntry(filterEntrySpec);
				tupleFilterEntries.push(tuplePartFilterEntry);
			}.bind(this));

			//And each of the pieces that form the dataPoint together (eg: year=2005 and product=sales)
			var andSpec = {
				operator: 'and',
				values: tupleFilterEntries,
				visualizationId: this.visId
			};
			return new FilterEntry(andSpec);
		},

		_createFilterEntrySpec: function(filterEntryKey, filterDefinition) {
			var conditionsInfo = filterDefinition.conditionsInfo || {};
			return (!filterDefinition.operator && !filterDefinition.values && !filterDefinition.type) ? {
				columnId: filterEntryKey.columnId || filterEntryKey.itemId
			} : {
				columnId: filterEntryKey.columnId,
				values: filterDefinition.values,
				valueDataItem: filterDefinition.valueDataItem,
				type: filterDefinition.type,
				operator: filterDefinition.operator,
				visualizationId: this.visId, //Sure hope this is the widgetId not the visId!
				excludedValues: conditionsInfo.excludedValues,
				conditions: conditionsInfo.conditions,
				preOrPost: filterEntryKey.preOrPost,
				overrideHierarchyDefaultSelection: filterDefinition.overrideHierarchyDefaultSelection,
				filterBins: filterDefinition.filterBins,
				id: (filterDefinition.filterBins && filterDefinition.id) ? filterDefinition.id : filterEntryKey.id,
				binsLabel: filterDefinition.binsLabel,
				isMissing: filterDefinition.isMissing
			};
		},

		/**
			 * FILTERAPI:
			 * add a filter on this data source.
			 * This method is 'silent' meaning it is expected that the caller will use 'allFilterModificationComplete' if notification is necessary.
			 * @param filterEntryKey the column to filter which contains:
			 *				columnId: the columnId to filter
			 *				aggregationType: (only required for range filters)
			 *				preOrPost: 	(only required for range filters)
			 * 							if value is post, this filter will be a post-aggregation filter
			 *							if the value is pre, this filter will be a pre-aggregation filter
			 *							if unspecified, default rules will be applied
			 * @param filterDefinition which contains:
			 * 				operator*: for now 'eq' or 'in'.
			 * 				values*  if this is an array, an include filter...if simple value, an 'eq' filter.
			 * 				excludeValues (opt) for conditions filters values to exclude for a condition filter.
			 * 				conditions (opt) conditions spec to include in the query.
			 *				type (opt): if value is display, filter will use the display value (default visualization)
			 */
		updateFilterEntry: function(filterEntryKey, filterDefinition/*, options*/) {
			this._takeSnapShot();
			//Assign variables based on the definition.
			filterDefinition = filterDefinition || {};
			var conditionsInfo = filterDefinition && filterDefinition.conditionsInfo;

			this._checkFilterEntryKey(filterEntryKey);
			conditionsInfo = conditionsInfo || {};
			var filterEntry = this.getFilterEntry(filterEntryKey);
			if (!filterEntry || this.actionAllowsAdditionalFiltersPerItem(filterDefinition.action)) {
				//If the command is keepexclude, allow multiple filterEntries on the same baseId (but set a suffix)
				var filterEntrySpec = this._createFilterEntrySpec(filterEntryKey, filterDefinition);
				if (filterEntryKey.aggregationType) {
					filterEntrySpec.aggregationType = filterEntryKey.aggregationType;
				}

				filterEntry = this._createNewFilterEntry(filterDefinition, filterEntrySpec);
				this.add([filterEntry], {
					silent: true
				});
			} else {
				//If the filter entry is found, replace its values.  Currently only 1 filter per columnId
				//is allowed so a filter may change from a discrete to a range (aggregationType must be updated).
				//We should consider changing the filterkey to involve the aggregationtype so that we could allow
				//(eg) a local filter on a count within a discrete subset (this may be confusing to the user though).
				if (!filterEntry.isModelFilter) {
					const newFilterEntrySpec = {
						operator: filterDefinition.operator,
						type: filterDefinition.type,
						values: filterDefinition.values,
						aggregationType: filterEntryKey.aggregationType,
						conditions: conditionsInfo.conditions,
						layerId: filterDefinition.layerId,
						filterBins: filterDefinition.filterBins
					};

					if(conditionsInfo.excludedValues) {
						newFilterEntrySpec.excludedValues = conditionsInfo.excludedValues;
					}
					if (filterDefinition.valueDataItem) {
						newFilterEntrySpec.valueDataItem = filterDefinition.valueDataItem;
					}
					if (filterDefinition.visualizationId) {
						newFilterEntrySpec.visualizationId = filterDefinition.visualizationId;
					}

					filterEntry.set(newFilterEntrySpec, { silent: true });
				}
			}
			return filterEntry;
		},

		/**
			 * @param action a string denoting the filter action being performed (currently keepexclude or null)
			 * @returns true if this filter action allows multiple filters to be created against the same itemId (or dataPoint itemIds)
			 */
		actionAllowsAdditionalFiltersPerItem: function(action) {
			return (action === FilterEntry.prototype.FILTERACTION_KEEPEXCLUDE) ? true : false;
		},

		//Create a new filterEntry for the filter collection.
		//If the specified action (currently keepexclude) supports additionalFilters, set the idSuffix and id to be unique.
		_createNewFilterEntry: function(filterDefinition, filterEntrySpec) {
			if(filterDefinition.layerId){
				filterEntrySpec.layerId = filterDefinition.layerId;
			}

			if(filterDefinition.visualizationId){
				filterEntrySpec.visualizationId = filterDefinition.visualizationId;
			}

			var filterEntry = new FilterEntry(filterEntrySpec);

			//Ensure the id for the new filter entry is unique (in the case of keepexclude, there can be many ids against the same column.)
			if (this.actionAllowsAdditionalFiltersPerItem(filterDefinition.action)) {
				var idSuffix = 0;
				while (this.get(FilterEntry.prototype.determineIdValue(filterEntry.id, filterEntrySpec.aggregationType, idSuffix))) {
					idSuffix++;
				}
				if (idSuffix > 0) {
					filterEntry.idSuffix = idSuffix;
					filterEntry.id = FilterEntry.prototype.determineIdValue(filterEntry.id, filterEntrySpec.aggregationType, idSuffix);
				}
			}
			return filterEntry;
		},

		/**
		 * FILTERAPI
		 * Remove a filter entry
		 * This method is 'silent' meaning it is expected that the caller will use 'allFilterModificationComplete' if notification is necessary.
		 * The method also sets the 'updateNeeded' status
		 *
		 */
		removeFilterEntry: function(filterEntryKey) {
			const snapshot = this.toJSON();
			this._checkFilterEntryKey(filterEntryKey);
			var removed = !!this.remove(filterEntryKey, {
				silent: true
			});
			if (!removed) {
				// nothing to remove
				return false;
			}

			this._takeSnapShot(snapshot);

			return true;
		},

		clearCompoundFilters: function(oColumnsOrIds) {

			var possibleCompoundFilterIdValuePairs = {};
			oColumnsOrIds.forEach(function(oColumnOrId) {
				possibleCompoundFilterIdValuePairs[this._columnId(oColumnOrId)] = [];
			}.bind(this));
			//Now, fetch the datapoint filters and remove any that involve the columns or ids passed in.
			const localFilters = this.filter(filterEntry => {
				return (filterEntry.isCompound() && filterEntry.compoundFilterMatches(possibleCompoundFilterIdValuePairs));
			});
			_.each(localFilters, filterEntry => {
				this.removeFilterEntry(filterEntry);
			});
		},


		/**
			 * FILTERAPI:
			 * Load an external filtermap definition into the filter map (see toJSON.)
			 * (eg to restore the filterstate from the widgetModel to support load or undo/redo).
			 * @param oFilterSpec	A filterspec object to be loaded.
			 */
		fromJSON: function(oFiltersSpec, options) {
			this._takeSnapShot();
			this.reset(oFiltersSpec, options);
			this._compoundFiltersFromJSON();
		},

		/**
			 * FILTERAPI:(private)
			 * Note: This function abstracts the use of a column or a columnId, however;
			 * it will warn if a metadata column object is used in a filter interface as this is deprecated.
			 * The function will be updated in future to support VisColumn or columnId
			 * (and not support metadata columns).
			 * @param oColumnOrId	Either a column object or column Id for the column
			 * @returns a column id.  If the input is a column object, its columnId member
			 */
		_columnId: function(oColumnOrId) {
			if (oColumnOrId === undefined || oColumnOrId.columnId === undefined) {
				return oColumnOrId;
			}
			return oColumnOrId.columnId;
		},

		/**
			 * SELECTAPI:
			 * @returns the set of columnIds for simple selections (ones where the values are not more filter entries)....see compound below.
			 */
		getSelectedSimpleFilterColumnIds: function() {
			var selectedColumnIds = [];
			this.each(function(model) {
				if (!model.isCompound() && model.columnId !== undefined) {
					selectedColumnIds.push(model.columnId);
				}
			}.bind(this));
			return selectedColumnIds;
		},

		/**
			 * SELECTAPI:
			 * @returns the set of columnId's nested inside this compound filter.
			 *
			 * Example:
			 *	{ or:	{ and:	{ columnId: '1', operator: 'in', values: ['a', 'b', 'c'] },
			 *					{ columnId: '5', operator: 'in', values: ['d'] }
			 *          },
			 *          { and:	{ columnId: '5', operator: 'in', values: ['e'] }
			 *					{ columnId: '3', operator: 'in', values: ['f'] }
			 *          }
			 *	}
			 *	would return ['1','3','5']
			 *
			 */
		getSelectedCompoundFilterColumnIds: function() {
			var selectedColumnIdMap = {};
			this.each(function(model) {
				if (model.isCompound()) {
					model.loadSelectedColumnIdMap(selectedColumnIdMap);
				}
			}.bind(this));
			return _.keys(selectedColumnIdMap);
		},

		/**
			 * Compound values need to be post-processed when loaded fromJSON to allow a recursive model in a collection
			 * (where a model has a reference to another instance of itself).
			 */
		_compoundFiltersFromJSON: function() {
			this.each(function(filterEntry) {
				if (filterEntry.isCompound()) {
					filterEntry._compoundFilterFromJSON();
				}
			}.bind(this));
		},

		/**
		 * removes all of the filters that filtered on column(s) do not exist in the meta data manager anymore
		 * @param  {object} visMetadataManager
		 */
		cleanUpMissingFilterEntries: function(visMetadataManager){
			if(!(visMetadataManager && visMetadataManager.getMetadataColumn)){
				return;
			}
			var filterIdsToRemove = [];
			this.each(function(filterEntry) {
				if(filterEntry.isCompound()){
					var selectedColumnIdMap = {};
					filterEntry.loadSelectedColumnIdMap(selectedColumnIdMap);
					var hasMissingColumns = _.some(_.keys(selectedColumnIdMap), function(columnId) {
						return !visMetadataManager.getMetadataColumn(columnId);
					});
					if(hasMissingColumns){
						filterIdsToRemove.push(filterEntry.id);
					}
				} else if (filterEntry.columnId && !visMetadataManager.getMetadataColumn(filterEntry.columnId)) {
					filterIdsToRemove.push(filterEntry.id);
				}
			}.bind(this));
			// strip out unique filter ids in case there are duplicates
			filterIdsToRemove = _.uniq(filterIdsToRemove);
			_.each(filterIdsToRemove, function(filterId) {
				this.removeFilterEntry({
					id: filterId
				});
			}.bind(this));
		}
	});

	return Filters;
});
